import { Component } from '@theme/component';

/**
 * Loads a background video only when it becomes visible.
 * The preview image remains visible until the video can play.
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  connectedCallback() {
    super.connectedCallback();

    if (navigator.connection?.saveData) return;

    if (!('IntersectionObserver' in window)) {
      this.loadVideo();
      return;
    }

    this.videoObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        this.videoObserver?.disconnect();
        this.videoObserver = null;
        this.loadVideo();
      },
      { threshold: 0.01 }
    );

    this.videoObserver.observe(this);
  }

  disconnectedCallback() {
    this.videoObserver?.disconnect();
    this.videoObserver = null;
    super.disconnectedCallback();
  }

  loadVideo() {
    if (this.hasAttribute('data-video-loading')) return;
    this.setAttribute('data-video-loading', '');

    const { videoSources, videoElement } = this.refs;
    let hasSource = false;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) {
        source.setAttribute('src', videoSource);
        hasSource = true;
      }
    }

    if (!hasSource) return;

    const revealVideo = () => {
      this.setAttribute('data-video-ready', '');
      videoElement.play().catch(() => {
        // Keep the poster visible if autoplay is unavailable.
      });
    };

    videoElement.addEventListener('canplay', revealVideo, { once: true });
    videoElement.load();
    videoElement.play().catch(() => {
      // The canplay handler retries playback when enough data is available.
    });
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
